import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
} from "react"
import { useScrollDirection } from "../hooks/useScrollDirection"

// Crea il contesto
const BannerContext = createContext()

// Provider del contesto
export function BannerProvider({ children }) {
  const { scrollDirection, isTop, scrollY } = useScrollDirection()
  const [isBannerVisible, setIsBannerVisible] = useState(true)
  const [bannerHeight, setBannerHeight] = useState(0)
  const [isMobile, setIsMobile] = useState(false)
  const lastVisibleState = useRef(true)
  const animationFrameId = useRef(null)

  // Controlla se siamo su mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024)
    }

    checkMobile()
    window.addEventListener("resize", checkMobile)

    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  // Aggiorna visibilità banner in base allo scroll con RequestAnimationFrame
  // per sincronizzare con il refresh del browser e renderlo più fluido
  useEffect(() => {
    if (!isMobile) {
      // Su desktop il banner è sempre visibile
      setIsBannerVisible(true)
      return
    }

    // Funzione di aggiornamento con requestAnimationFrame
    const updateVisibility = () => {
      // Mostra sempre il banner in cima alla pagina
      if (isTop) {
        if (!lastVisibleState.current) {
          lastVisibleState.current = true
          setIsBannerVisible(true)
        }
        return
      }

      // Nascondi banner quando scorri verso il basso
      if (scrollDirection === "down" && lastVisibleState.current) {
        lastVisibleState.current = false
        setIsBannerVisible(false)
      }
      // Mostra banner quando scorri verso l'alto (ma non siamo in cima)
      else if (scrollDirection === "up" && !lastVisibleState.current) {
        lastVisibleState.current = true
        setIsBannerVisible(true)
      }
    }

    // Cancel any pending animation frame
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current)
    }

    // Schedule update on next animation frame
    animationFrameId.current = requestAnimationFrame(updateVisibility)

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current)
      }
    }
  }, [scrollDirection, isTop, isMobile])

  const value = {
    isBannerVisible,
    bannerHeight,
    setBannerHeight,
    isMobile,
  }

  return (
    <BannerContext.Provider value={value}>{children}</BannerContext.Provider>
  )
}

// Hook personalizzato per usare il contesto
export function useBanner() {
  const context = useContext(BannerContext)
  if (context === undefined) {
    throw new Error(
      "useBanner deve essere usato all'interno di un BannerProvider"
    )
  }
  return context
}
